import type { InvocationContext, RuntimeContext } from 'hypertube-nodejs-sdk'
import type { StoreApi } from 'zustand'

import { UnifiedGraftModelTypeKey } from '@/core/const/unifiedGraftModelTypeKeys.const'
import type {
  ExecutableMember,
  ExecuteMemberParams,
  ExecutionParams,
  FieldExecutionParams,
  InstanceParams,
} from '@/core/types/library.type'
import { isGlobalMethodType } from '@/core/utils/isGlobalMethodType'

import { appendLog, getContext } from './helpers'
import type { LibraryActions, LibraryStore } from './types'
import { formatExecutionResult } from './utils/formatExecutionResult'
import { getClassPath } from './utils/getClassPath'
import { getFieldReadLabel } from './utils/getFieldReadLabel'
import { runMemberInvocation } from './utils/runMemberInvocation'
import { unpackInvocationResult } from './utils/unpackInvocationResult'

function formatResultLogLine(formatted: string): string {
  return formatted.includes('\n') ? `Result:\n${formatted}` : `Result: ${formatted}`
}

function formatExecutionError(error: unknown): string {
  const message = String(error ?? '')
  const isAssemblyLoad =
    message.includes('FileNotFoundException') ||
    message.includes('Could not load file or assembly') ||
    message.includes('Nie można odnaleźć określonego pliku')
  const hint = isAssemblyLoad
    ? ' This usually means a required assembly or package is not available in the execution environment (e.g. gateway).'
    : ''
  return `Error trying to execute ${message}${hint}`
}

type SetState = StoreApi<LibraryStore>['setState']
type GetState = StoreApi<LibraryStore>['getState']

function getTypeContext(
  runtimeContext: RuntimeContext,
  runtimeName: ExecuteMemberParams['runtimeName'],
  classData: ExecuteMemberParams['classData']
): InvocationContext {
  return runtimeContext.getType(getClassPath(runtimeName, classData))
}

function needsInstance(member: ExecutableMember): boolean {
  if (member.kind === 'constructor') return true
  if (member.kind === 'method') return !member.isStatic && !isGlobalMethodType(member.method.type)
  return member.field.type === UnifiedGraftModelTypeKey.INSTANCE_FIELD && !member.field.isStatic
}

function isGlobalMember(member: ExecutableMember): boolean {
  if (member.kind === 'field') return member.field.type === UnifiedGraftModelTypeKey.GLOBAL_FIELD
  if (member.kind === 'method') return isGlobalMethodType(member.method.type)
  return false
}

function formatLogParam(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) return String(value)
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

function formatInstanceLog(
  className: string,
  constructorKey: string | undefined,
  instanceParams: ReadonlyArray<unknown>
): string {
  const ctor = constructorKey ? ` [${constructorKey}]` : ''
  const params =
    instanceParams.length > 0
      ? ` with parameters: ${instanceParams.map(formatLogParam).join(', ')}`
      : ' without parameters'
  return `Creating instance of class ${className} with constructor${ctor}${params}`
}

function formatActionLog(member: ExecutableMember, methodParams: ReadonlyArray<unknown>): string | undefined {
  if (member.kind === 'field') {
    return `Reading ${getFieldReadLabel(member.field)} ${member.field.name}`
  }

  if (member.kind === 'method') {
    const params =
      methodParams.length > 0 ? `with parameters: ${methodParams.map(formatLogParam).join(', ')}` : 'without parameters'
    return `Executing method ${member.method.name} [${member.methodKey}] ${params}`
  }

  return undefined
}

export const createLibraryActions = (set: SetState, get: GetState): LibraryActions => {
  const executeMember = async ({
    runtimeName,
    classData,
    member,
    resultKey,
    methodParams = [],
    instanceParams = [],
    constructorKey,
  }: ExecuteMemberParams) => {
    const { gatewayPath, gatewayWsPort, isSecured } = get()

    try {
      const runtimeContext = await getContext({
        runtimeName,
        gatewayPath,
        gatewayPort: gatewayWsPort,
        isSecured,
      })

      const typeContext = isGlobalMember(member) ? undefined : getTypeContext(runtimeContext, runtimeName, classData)

      if (needsInstance(member)) {
        set((state: LibraryStore) => ({
          ...state,
          logs: appendLog(state, resultKey, formatInstanceLog(classData.name, constructorKey, instanceParams)),
        }))
      }

      const actionLog = formatActionLog(member, methodParams)
      if (actionLog) {
        set((state: LibraryStore) => ({
          ...state,
          logs: appendLog(state, resultKey, actionLog),
        }))
      }

      const execution = await runMemberInvocation({
        runtimeContext,
        typeContext,
        member,
        methodParams,
        instanceParams,
        constructorKey,
      })

      const unpacked = await unpackInvocationResult(execution, {
        libraries: get().libraries,
        fallbackClassData: member.kind === 'constructor' ? classData : undefined,
        hintTypeName:
          member.kind === 'method'
            ? member.method.returns
            : member.kind === 'field'
              ? member.field.fieldType
              : undefined,
      })
      const resultValue = formatExecutionResult(unpacked)

      set((state: LibraryStore) => ({
        ...state,
        results: {
          ...state.results,
          [resultKey]: resultValue,
        },
        logs: appendLog(state, resultKey, formatResultLogLine(resultValue)),
      }))
    } catch (error) {
      set((state: LibraryStore) => ({
        ...state,
        logs: appendLog(state, resultKey, formatExecutionError(error)),
      }))
    }
  }

  return {
    executeMember,

    executeField: ({ runtimeName, classData, field, instanceParams = [], constructorKey }: FieldExecutionParams) =>
      executeMember({
        runtimeName,
        classData,
        member: { kind: 'field', field },
        resultKey: `field-${field.name}`,
        instanceParams,
        constructorKey,
      }),

    executeMethod: ({
      runtimeName,
      classData,
      methodData,
      methodKey,
      isStatic,
      params,
      instanceParams,
      constructorKey,
    }: ExecutionParams) =>
      executeMember({
        runtimeName,
        classData,
        member: { kind: 'method', method: methodData, methodKey, isStatic },
        resultKey: methodKey,
        methodParams: params,
        instanceParams,
        constructorKey,
      }),

    executeConstructor: ({ runtimeName, classData, instanceParams, constructorKey }: InstanceParams) =>
      executeMember({
        runtimeName,
        classData,
        member: { kind: 'constructor' },
        resultKey: constructorKey ?? classData.name,
        instanceParams,
        constructorKey,
      }),

    clearResult: (name: string) => {
      set((state: LibraryStore) => ({
        ...state,
        results: {
          ...state.results,
          [name]: '',
        },
        logs: {
          ...state.logs,
          [name]: [],
        },
      }))
    },
  }
}
